import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// css and images
import Close from "../../assets/close.svg";

// components
import DropDown from "../../formComponents/DropDown";
import FormButton from "../../formComponents/FormButton";
import InlineInput from "../../formComponents/InlineInput";
import AddCompanyPopup from "../../components/AddCompanyPopup";

// global imports
import { companyStore, messageAndErrorStore, userStore } from "../../store/Store";

// hooks
import useGetFetch from "../../hooks/useGetFetch";
import { useEscape } from "../../hooks/useEscape";
import useGetFetchFromId from "../../hooks/useGetFetchFromId";

export default function AddEditCompanyCarrier({ docId, setTrigger, setDocId }) {
  // global
  const JwtToken = userStore((data) => data.JwtToken);
  const currentCompany = userStore((data) => data.currentCompany);
  const currentUser = userStore((data) => data.currentUser);
  const setAuthStatus = userStore((data) => data.setAuthStatus);
  const setCompanyCarriers = companyStore((data) => data.setCompanyCarriers);
  const companyCarriers = companyStore((data) => data.companyCarriers);
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)

  // local
  var id = 0;
  if (docId) {
    id = docId;
  }
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [pcCarriers, setPcCarriers] = useState(null);
  const [carrierOptions, setCarrierOptions] = useState([]);
  const [companyExists, setCompanyExists] = useState(true);
  const [newCarrier, setNewCarrier] = useState({
    id,
    owner_company_id: currentCompany.id,
    company_id: 0,
    pc_carrier_id: "",
    carrier_name: "",
    rate_percentage: 0,
    created_by_id: currentUser.id,
    updated_by_id: currentUser.id,
  });

  const handleClose = () => {
    setTrigger(false);
    setDocId("");
  };

  useEscape(setTrigger, [setDocId], [""]);

  const handleChange = (e) => {
    setNewCarrier({
      ...newCarrier,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelect = (o, e) => {
    setNewCarrier({
      ...newCarrier,
      [e.name]: o.value,
      carrier_name: o.label,
    });
  };

  // check if company exists
  useEffect(() => {
    if (newCarrier.pc_carrier_id !== "") {
      const controller = new AbortController();
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + JwtToken);
      const requestOptions = {
        method: "GET",
        headers,
        signal: controller.signal,
      };
      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/company-exists?pc_carrier_id=${newCarrier.pc_carrier_id}&company_type=2`,
        requestOptions
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              navigate("/login");
              setAuthStatus(false);
            } else {
              toggleNewMessage(`Something Went Wrong: ${res.status}`);
              return Promise.resolve();
            }
          }
          return res.json();
        })
        .then((data) => {
          if (data !== null) {
            if (data !== 0) {
              setNewCarrier((curr) => ({
                ...curr,
                company_id: data,
              }));
            } else {
              setCompanyExists(false);
            }
          } else {
            return;
          }
        })
        .catch((_) => {
          toggleNewMessage("Something went wrong");
        });
      return () => controller.abort();
    }
  }, [
    newCarrier.pc_carrier_id,
    JwtToken,
    navigate,
    setAuthStatus,
    toggleNewMessage,
  ]);

  // get pc carriers
  const { isPending: pcPending, error: pcError } = useGetFetch(
    `${process.env.REACT_APP_BACKEND}/pc/pc-company-carriers`,
    setPcCarriers
  );

  const { error } = useGetFetchFromId(
    `${process.env.REACT_APP_BACKEND}/pc/company-carriers/${id}`,
    setNewCarrier,
    id
  );

  // get carrier options
  useEffect(() => {
    const options = [];
    if (pcCarriers && !pcError) {
      pcCarriers.forEach((group) => {
        group.data.forEach((c) => {
          var idx = -1;
          if (companyCarriers) {
            idx = companyCarriers.findIndex((x) => x.pc_carrier_id === c.id);
          }
          if (c.status === 1 && idx === -1 && id === 0) {
            options.push({ value: c.id, label: c.name });
          } else if (id > 0) {
            options.push({ value: c.id, label: c.name });
          }
        });
      });
    }
    setCarrierOptions(options);
  }, [pcCarriers, companyCarriers, id, pcError]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setPending(true);
    var reqBody = newCarrier;
    var method = "POST";
    if (id > 0) {
      method = "PATCH";
      reqBody.updated_by_id = currentUser.id;
    }
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    reqBody.rate_percentage = parseInt(reqBody.rate_percentage);
    const requestOptions = {
      method,
      headers,
      body: JSON.stringify(reqBody),
    };

    fetch(
      `${process.env.REACT_APP_BACKEND}/pc/company-carrier/${reqBody.id}`,
      requestOptions
    )
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401) {
            setPending(false);
            setAuthStatus(false);
            navigate("/login");
            return;
          } else {
            toggleNewMessage("Could not add Carrier");
            setPending(false);
            return;
          }
        } else {
          return res.json();
        }
      })
      .then((data) => {
        if (!data) {
          console.log(data);
          toggleNewMessage("Could not add Carrier");
        } else {
          if (id > 0) {
            toggleNewMessage(null, `Carrier Updated`);
          } else {
            toggleNewMessage(null, `Carrier Added`);
          }
          setCompanyCarriers(data);
          setTrigger(false);
          setDocId(null);
        }
        setPending(false);
        return;
      })
      .catch((err) => {
        console.log(err);
        toggleNewMessage("Could not add Carrier");
        setPending(false);
        return;
      });
  };

  return (
    !pcPending && (
      <div className="popup">
        <div className="popup-inner">
          {!pending && (
            <div className="close">
              <img onClick={handleClose} src={Close} alt="close" />
            </div>
          )}
          <h3>Add Carrier</h3>
          {!pcError && !error ? (
            <form onSubmit={handleSubmit}>
              <DropDown
                name={"pc_carrier_id"}
                classThree={"col-sm-9"}
                title={"Carrier"}
                options={carrierOptions}
                onChange={handleSelect}
                className={"dropdown"}
                maxMenuHeight={200}
                required={true}
                value={carrierOptions.filter(
                  (x) => x.value === newCarrier.pc_carrier_id
                )}
                classOne="mb-3 row field"
                classTwo={"col-sm-2 col-form-label bold-label"}
                placeholder={"Carrier"}
              />
              <InlineInput
                type={"number"}
                classname={"col-form-label bold-label col-auto"}
                name={"rate_percentage"}
                title={"Rate Percentage"}
                placeholder={"Pay Percentage if Applicable"}
                onChange={handleChange}
                value={newCarrier.rate_percentage || ''}
                required={false}
              />
              {!companyExists && (
                <AddCompanyPopup
                  setTrigger={setCompanyExists}
                  companyTypeId={2}
                  companyName={newCarrier.carrier_name}
                  pcCarrierId={newCarrier.pc_carrier_id}
                  pcDriverId={""}
                  setCompany={setNewCarrier}
                  company={newCarrier}
                />
              )}
              <FormButton name={"Submit"} pending={pending} />
            </form>
          ) : (
            <p className="error">Something went wrong</p>
          )}
        </div>
      </div>
    )
  );
}
